/* Kai's Jig Lab: independent Stinger Minnow tail, v1.0.
   Extends the shared builder in main-product.liquid without duplicating its
   palettes, profile/variant handling, sticky preview, or cart implementation. */
(() => {
  'use strict';
  if (window.__kaiStingerTailLoaded) return;
  window.__kaiStingerTailLoaded = true;
  const SVG_NS = 'http://www.w3.org/2000/svg';
  let sequence = 0;

  function install(builder) {
    if (builder.hasAttribute('data-kai-stinger-tail-ready') || !builder.preview) return;
    const options = builder.querySelector('.custom-builder-options');
    const firstColorStep = builder.querySelector('[data-first-color-label]')?.closest('fieldset');
    const secondColorStep = builder.querySelector('[data-second-color-label]')?.closest('fieldset');
    const firstFlakeStep = builder.querySelector('[data-first-flake-label]')?.closest('fieldset');
    const secondFlakeStep = builder.querySelector('[data-second-flake-label]')?.closest('fieldset');
    const svg = builder.preview.querySelector('.stinger-preview__svg');
    const topShape = svg?.querySelector('.stinger-preview__top');
    const topFlake = svg?.querySelector('[data-stinger-flake="first"]');
    const legend = builder.preview.querySelector('.jig-preview__legend');
    if (!options || !firstColorStep || !secondColorStep || !firstFlakeStep || !secondFlakeStep || !topShape || !topFlake || !legend) return;

    const originalSteps = Array.from(options.children);
    const originalNumbers = originalSteps.map(step => step.querySelector('legend > span')?.textContent);
    const notesHeading = builder.querySelector('.custom-builder-notes > span > b');
    const originalNotesHeading = notesHeading?.textContent;
    const uid = `kai-stinger-tail-${++sequence}`;
    const isStinger = () => builder.preview.dataset.previewProfile === 'stinger';
    const active = () => Boolean(builder.isActive && isStinger());

    // Clone the existing option controls so the tail always uses the same palette
    // and the same None / Flake / Glitter choices as the rest of this builder.
    const tailColorStep = secondColorStep.cloneNode(true);
    tailColorStep.setAttribute('data-stinger-tail-only', '');
    tailColorStep.hidden = true;
    const colorHeading = tailColorStep.querySelector('[data-second-color-label]');
    colorHeading.removeAttribute('data-second-color-label');
    colorHeading.setAttribute('data-stinger-tail-color-label', '');
    colorHeading.textContent = 'Tail Color';
    tailColorStep.querySelectorAll('[data-custom-color]').forEach(input => {
      input.name = `${uid}-color`;
      input.dataset.customColor = 'stinger-tail';
      input.checked = false;
      input.defaultChecked = false;
      input.disabled = true;
    });
    tailColorStep.querySelectorAll('[data-custom-interference]').forEach(input => {
      input.name = `${uid}-interference`;
      input.dataset.customInterference = 'stinger-tail';
      input.checked = input.value === 'None';
      input.defaultChecked = input.checked;
      input.disabled = true;
    });
    const tailFlakeStep = secondFlakeStep.cloneNode(true);
    tailFlakeStep.setAttribute('data-stinger-tail-only', '');
    tailFlakeStep.hidden = true;
    const flakeHeading = tailFlakeStep.querySelector('[data-second-flake-label]');
    flakeHeading.removeAttribute('data-second-flake-label');
    flakeHeading.setAttribute('data-stinger-tail-flake-label', '');
    flakeHeading.textContent = 'Tail Flake / Glitter';
    tailFlakeStep.querySelectorAll('input').forEach(input => {
      input.name = `${uid}-flake`;
      input.dataset.customFlake = 'stinger-tail';
      input.checked = input.value === 'None';
      input.defaultChecked = input.checked;
      input.disabled = true;
    });
    options.append(tailColorStep, tailFlakeStep);

    const makeProperty = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.disabled = true;
      input.setAttribute('data-custom-property', '');
      input.setAttribute('data-stinger-tail-property', '');
      builder.append(input);
      return input;
    };
    const tailProperty = makeProperty('Tail Color', '');
    const tailFlakeProperty = makeProperty('Tail Flake', 'None');
    const tailInterferenceProperty = makeProperty('Tail Interference Color', 'None');
    const tailColor = () => tailColorStep.querySelector('[data-custom-color="stinger-tail"]:checked')?.value || '';
    const tailFlake = () => tailFlakeStep.querySelector('input:checked')?.value || 'None';
    const tailInterference = () => tailColorStep.querySelector('[data-custom-interference="stinger-tail"]:checked')?.value || 'None';

    // Keep the exact existing silhouette. Split at x=720, where the belly stops;
    // clip both the top paint AND its flakes so neither can bleed into the tail.
    const makeClip = (suffix, x, width) => {
      const clip = document.createElementNS(SVG_NS, 'clipPath');
      clip.id = `${uid}-${suffix}`;
      clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
      const rect = document.createElementNS(SVG_NS, 'rect');
      Object.entries({x, y: 0, width, height: 500}).forEach(([key, value]) => rect.setAttribute(key, String(value)));
      clip.append(rect);
      svg.querySelector('defs').append(clip);
      return `url(#${clip.id})`;
    };
    const bodyClip = makeClip('body-clip', 0, 720);
    const tailClip = makeClip('tail-clip', 720, 680);
    const tailShape = topShape.cloneNode(false);
    tailShape.setAttribute('class', 'stinger-preview__tail');
    tailShape.setAttribute('clip-path', tailClip);
    const tailOverlay = topFlake.cloneNode(false);
    tailOverlay.setAttribute('class', 'stinger-preview__flake stinger-preview__flake--tail');
    tailOverlay.setAttribute('data-stinger-flake', 'tail');
    tailOverlay.setAttribute('clip-path', tailClip);
    tailOverlay.style.fill = 'none';
    topShape.setAttribute('clip-path', bodyClip);
    topFlake.setAttribute('clip-path', bodyClip);
    topFlake.after(tailShape, tailOverlay);

    const tailLegend = document.createElement('span');
    tailLegend.setAttribute('data-stinger-tail-only', '');
    const dot = document.createElement('i');
    dot.className = 'jig-preview__dot jig-preview__dot--stinger-tail';
    const tailLabel = document.createElement('b');
    tailLabel.setAttribute('data-preview-stinger-tail-label', '');
    tailLegend.append(dot, tailLabel);
    const tailFlakeLegend = document.createElement('span');
    tailFlakeLegend.setAttribute('data-stinger-tail-only', '');
    tailFlakeLegend.className = 'jig-preview__flake-item';
    const tailFlakeDot = document.createElement('i');
    tailFlakeDot.className = 'jig-preview__dot jig-preview__flake-dot custom-flake-chip__swatch--none';
    tailFlakeDot.setAttribute('data-preview-stinger-tail-flake-swatch', '');
    tailFlakeDot.setAttribute('aria-hidden', 'true');
    const tailFlakeLabel = document.createElement('b');
    tailFlakeLabel.setAttribute('data-preview-stinger-tail-flake-label', '');
    tailFlakeLegend.append(tailFlakeDot, tailFlakeLabel);

    const tailInterferenceLegend = document.createElement('span');
    tailInterferenceLegend.setAttribute('data-stinger-tail-only', '');
    tailInterferenceLegend.className = 'jig-preview__interference-item';
    const tailInterferenceDot = document.createElement('i');
    tailInterferenceDot.className = 'jig-preview__dot jig-preview__interference-dot custom-interference-chip__swatch--none';
    tailInterferenceDot.setAttribute('data-preview-stinger-tail-interference-swatch', '');
    tailInterferenceDot.setAttribute('aria-hidden', 'true');
    const tailInterferenceLabel = document.createElement('b');
    tailInterferenceLabel.setAttribute('data-preview-stinger-tail-interference-label', '');
    tailInterferenceLegend.append(tailInterferenceDot, tailInterferenceLabel);

    const firstFlakeLegend = legend.querySelector('[data-preview-first-flake-swatch]')?.closest('span');
    if (firstFlakeLegend) legend.insertBefore(tailLegend, firstFlakeLegend);
    else legend.append(tailLegend);
    legend.append(tailFlakeLegend);
    legend.append(tailInterferenceLegend);

    const syncEnabled = () => {
      const enabled = active();
      tailProperty.disabled = !enabled;
      tailFlakeProperty.disabled = !enabled;
      tailInterferenceProperty.disabled = !enabled;
      tailColorStep.disabled = !enabled;
      tailFlakeStep.disabled = !enabled;
      tailColorStep.querySelectorAll('input').forEach(input => { input.disabled = !enabled; });
      tailFlakeStep.querySelectorAll('input').forEach(input => { input.disabled = !enabled; });
    };
    const syncValues = () => {
      tailProperty.value = tailColor();
      tailFlakeProperty.value = tailFlake();
      tailInterferenceProperty.value = tailInterference();
      syncEnabled();
    };
    const syncTailFlakeSwatch = (value) => {
      Array.from(tailFlakeDot.classList).forEach((className) => {
        if (className.startsWith('custom-flake-chip__swatch--')) tailFlakeDot.classList.remove(className);
      });
      const slug = (value || 'None').toLowerCase().replace(/\s+/g, '-');
      tailFlakeDot.classList.add(`custom-flake-chip__swatch--${slug}`);
    };
    const syncTailInterferenceSwatch = (value) => {
      Array.from(tailInterferenceDot.classList).forEach((className) => {
        if (className.startsWith('custom-interference-chip__swatch--')) tailInterferenceDot.classList.remove(className);
      });
      const slug = (value || 'None').toLowerCase().replace(/\s+/g, '-');
      tailInterferenceDot.classList.add(`custom-interference-chip__swatch--${slug}`);
    };
    const tailInterferencePalette = {
      Red: '#ef5365',
      Blue: '#5fbfff',
      Gold: '#e2b83f',
      Violet: '#a777e8',
      Green: '#68cf7b'
    };
    const renderTail = () => {
      syncValues();
      tailLabel.textContent = tailProperty.value ? `Tail Color: ${tailProperty.value}` : 'Tail Color';
      tailFlakeLabel.textContent = `Tail Flake: ${tailFlakeProperty.value}`;
      tailInterferenceLabel.textContent = `Tail Interference Color: ${tailInterferenceProperty.value}`;
      syncTailFlakeSwatch(tailFlakeProperty.value);
      syncTailInterferenceSwatch(tailInterferenceProperty.value);
      if (!isStinger()) return;
      const tailPaint = tailProperty.value ? builder.previewPaint(tailProperty.value) : (tailInterferenceProperty.value !== 'None' ? (tailInterferencePalette[tailInterferenceProperty.value] || '#0877f9') : '#0877f9');
      builder.preview.style.setProperty('--stinger-tail-paint', tailPaint);
      builder.applyStingerFlake('tail', tailFlakeProperty.value);

    };
    let previousMode;
    const refresh = () => {
      const stinger = isStinger();
      [tailColorStep, tailFlakeStep, tailLegend, tailFlakeLegend, tailInterferenceLegend].forEach(node => { node.hidden = !stinger; });
      if (stinger !== previousMode) {
        if (stinger) {
          const steps = [firstColorStep, firstFlakeStep, secondColorStep, secondFlakeStep, tailColorStep, tailFlakeStep];
          steps.forEach((step, index) => {
            options.append(step);
            const number = step.querySelector('legend > span');
            if (number) number.textContent = String(index + 2);
          });
          if (notesHeading) notesHeading.textContent = '8. Custom color notes';
        } else {
          originalSteps.forEach((step, index) => {
            options.append(step);
            const number = step.querySelector('legend > span');
            if (number && originalNumbers[index] !== undefined) number.textContent = originalNumbers[index];
          });
          options.append(tailColorStep, tailFlakeStep);
          if (notesHeading) notesHeading.textContent = originalNotesHeading;
        }
        previousMode = stinger;
      }
      renderTail();
    };

    // Existing event listeners call these methods on their builder instance.
    // Keep the shared implementation and add only the Stinger-specific behavior.
    const wrap = (name, replacement) => {
      const original = builder[name].bind(builder);
      builder[name] = (...args) => replacement(original, ...args);
    };
    wrap('enableProperties', original => { original(); syncEnabled(); });
    wrap('updatePreviewProfile', (original, profile) => { original(profile); refresh(); });
    wrap('closeBuilder', original => { original(); syncEnabled(); });
    wrap('needsNotes', original => original());
    wrap('validState', original => {
      const state = original();
      if (!isStinger()) return state;
      const tailReady = Boolean(tailColor()) || tailInterference() !== 'None';
      const colorsReady = state.colorsReady && tailReady;
      return { ...state, colorsReady, valid: state.profileReady && colorsReady && state.notesReady };
    });
    wrap('syncSubmissionProperties', original => { original(); syncValues(); });
    wrap('injectSubmissionProperties', (original, formData) => {
      original(formData);
      if (!builder.isActive || !formData) return;
      if (isStinger()) {
        formData.set('properties[Tail Color]', tailColor());
        formData.set('properties[Tail Flake]', tailFlake());
        formData.set('properties[Tail Interference Color]', tailInterference());
      } else if (builder.preview.dataset.previewProfile !== 'fluke') {
        // The Fluke already has its own Tail properties; never remove those.
        formData.delete('properties[Tail Color]');
        formData.delete('properties[Tail Flake]');
        formData.delete('properties[Tail Interference Color]');
      }
    });
    wrap('validateSubmit', (original, event) => {
      if (!active()) return original(event);
      builder.syncSubmissionProperties();
      const state = builder.validState();
      if (state.valid) return;
      event.preventDefault();
      const missing = [];
      if (!builder.headProperty?.value && !(builder.firstInterferenceProperty?.value && builder.firstInterferenceProperty.value !== 'None')) missing.push('Top Color or Interference Color');
      if (!builder.bodyProperty?.value && !(builder.secondInterferenceProperty?.value && builder.secondInterferenceProperty.value !== 'None')) missing.push('Belly Color or Interference Color');
      if (!tailColor() && tailInterference() === 'None') missing.push('Tail Color or Interference Color');
      let message = 'Choose your ' + missing.join(', ') + '.';
      if (!state.profileReady) message = 'Choose a jig profile.';
      else if (state.colorsReady && !state.notesReady) message = 'Add a note describing the Custom Match color you want.';
      builder.showError(message);
    });

    tailColorStep.addEventListener('change', () => { renderTail(); builder.syncValidity(); });
    tailFlakeStep.addEventListener('change', renderTail);
    builder.setAttribute('data-kai-stinger-tail-ready', '1');
    refresh();
    builder.syncValidity();
  }

  customElements.whenDefined('custom-jig-builder').then(() => {
    const scan = root => {
      if (root.nodeType !== 1 && root.nodeType !== 9) return;
      if (root.matches?.('custom-jig-builder')) install(root);
      root.querySelectorAll('custom-jig-builder').forEach(install);
    };
    scan(document);
    // Covers section reloads in the theme editor and dynamically inserted views.
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(scan));
    }).observe(document.body, {childList: true, subtree: true});
  });
})();
